import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, ChevronDown, ChevronRight, ClipboardList, ExternalLink, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { usePermission } from '../../../components/rbac/use-permission';
import { P } from '../../../lib/rbac/permissions.generated';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import {
  abnahmeApi,
  LAUF_ART_LABEL,
  type LaeufeDto,
  type LaufArt,
  type LaufDto,
  type LaufErgebnisDto,
} from '../../../lib/api/abnahme';

const zeit = (iso: string | null): string => {
  if (!iso) return '—';
  const datum = new Date(iso);
  return Number.isNaN(datum.getTime()) ? '—' : datum.toLocaleString('de-DE');
};

const ARTEN: Array<LaufArt | 'alle'> = ['alle', 'ci_backend', 'ci_frontend', 'e2e_smoke', 'manuell'];

/**
 * Testläufe der CI: Historie, Trend, verwaiste und wackelnde Tests
 * (docs/abnahme-plan.md, Phase 3).
 *
 * Gezeigt wird, was die CI meldet — jeder Push auf dev und main, auch rote.
 * Einzelergebnisse gibt es nur für getaggte und rote Tests.
 */
export default function TestlaeufePage() {
  const navigate = useNavigate();
  const darfDurchfuehren = usePermission(P.abnahme.testlaeufe.testlaeufe_durchfuehren);
  const [neu, setNeu] = useState<{ titel: string; version: string; schluessel: string } | null>(null);
  const [legtAn, setLegtAn] = useState(false);
  const [art, setArt] = useState<LaufArt | 'alle'>('alle');
  const [daten, setDaten] = useState<LaeufeDto | null>(null);
  const [laedt, setLaedt] = useState(true);
  const [offen, setOffen] = useState<number | null>(null);
  const [details, setDetails] = useState<Record<number, LaufErgebnisDto[]>>({});

  const laden = useCallback(async () => {
    setLaedt(true);
    try {
      setDaten(await abnahmeApi.laeufe(art === 'alle' ? undefined : art, 60));
    } catch (fehler) {
      toast.error(fehler instanceof Error ? fehler.message : 'Die Testläufe ließen sich nicht laden.');
    } finally {
      setLaedt(false);
    }
  }, [art]);

  useEffect(() => {
    void laden();
  }, [laden]);

  const manuellAnlegen = async () => {
    if (!neu) return;
    setLegtAn(true);
    try {
      // Schlüssel („PERS-3, KUNDE-1") in Story-IDs übersetzen; leer heißt: alle bereitstehenden.
      const gewuenscht = neu.schluessel
        .split(/[\s,;]+/)
        .map((teil) => teil.trim().toUpperCase())
        .filter(Boolean);
      let storyIds: number[] = [];
      if (gewuenscht.length > 0) {
        const katalog = await abnahmeApi.katalog();
        const alle = (katalog?.epics ?? []).flatMap((epic) => epic.stories);
        const unbekannt = gewuenscht.filter((key) => !alle.some((story) => story.key === key));
        if (unbekannt.length > 0) {
          toast.error(`Unbekannte Stories: ${unbekannt.join(', ')}`);
          return;
        }
        storyIds = alle.filter((story) => gewuenscht.includes(story.key)).map((story) => story.id);
      }
      const lauf = await abnahmeApi.manuellerLauf({
        titel: neu.titel.trim(),
        version: neu.version.trim() || undefined,
        storyIds,
      });
      if (lauf) navigate(`/abnahme/testlaeufe/${lauf.id}`);
    } catch (fehler) {
      toast.error(fehler instanceof Error ? fehler.message : 'Der Lauf ließ sich nicht anlegen.');
    } finally {
      setLegtAn(false);
    }
  };

  const e2eUebernehmen = async () => {
    try {
      const ergebnis = await abnahmeApi.e2eTestfaelleUebernehmen();
      toast.success(
        `${ergebnis?.angelegt ?? 0} Testfälle aus dem E2E-Testplan angelegt` +
          (ergebnis?.ohneStory?.length ? ` — ohne passende Story: ${ergebnis.ohneStory.join(', ')}` : '.'),
      );
    } catch (fehler) {
      toast.error(fehler instanceof Error ? fehler.message : 'Übernahme fehlgeschlagen.');
    }
  };

  const aufklappen = async (lauf: LaufDto) => {
    if (lauf.art === 'manuell') {
      navigate(`/abnahme/testlaeufe/${lauf.id}`);
      return;
    }
    if (offen === lauf.id) {
      setOffen(null);
      return;
    }
    setOffen(lauf.id);
    if (!details[lauf.id]) {
      try {
        const detail = await abnahmeApi.lauf(lauf.id);
        setDetails((vorher) => ({ ...vorher, [lauf.id]: detail?.ergebnisse ?? [] }));
      } catch {
        setDetails((vorher) => ({ ...vorher, [lauf.id]: [] }));
      }
    }
  };

  const laeufe = useMemo(() => daten?.items ?? [], [daten]);
  // Trend: älteste links, damit die Leiste wie eine Zeitachse liest.
  const trend = useMemo(() => [...laeufe].reverse().slice(-40), [laeufe]);
  const hoechstes = Math.max(1, ...trend.map((lauf) => lauf.bestanden + lauf.fehlgeschlagen));

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <div>
        <h1 className="text-2xl font-semibold leading-7">Testläufe</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Was die CI bei jedem Push meldet. Tests mit Story-Schlüssel färben im Katalog ihre
          Kriterien; rote Tests stehen hier mit Meldung, auch ohne Schlüssel.
        </p>
      </div>

      {darfDurchfuehren && (
        <div className="flex flex-wrap gap-2">
          <Button
            size="sm"
            className="gap-1.5"
            onClick={() => setNeu({ titel: '', version: '', schluessel: '' })}
          >
            <ClipboardList className="h-4 w-4" /> Manueller Lauf
          </Button>
          <Button size="sm" variant="outline" onClick={() => void e2eUebernehmen()}>
            Testfälle aus dem E2E-Testplan übernehmen
          </Button>
        </div>
      )}

      {neu && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Neuer manueller Lauf</CardTitle>
            <CardDescription>
              Enthält alle Testfälle der gewählten Stories. Ohne Auswahl: alle Stories, die zur
              Abnahme bereitstehen.
            </CardDescription>
          </CardHeader>
          <CardContent className="grid gap-3 sm:grid-cols-3">
            <div className="space-y-1 sm:col-span-3">
              <Label htmlFor="lauf-titel">Titel</Label>
              <Input
                id="lauf-titel"
                value={neu.titel}
                placeholder="z. B. Abnahme Runde 1"
                onChange={(event) => setNeu({ ...neu, titel: event.target.value })}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="lauf-version">Version auf dem Testsystem</Label>
              <Input
                id="lauf-version"
                value={neu.version}
                placeholder="z. B. 0.0.580"
                onChange={(event) => setNeu({ ...neu, version: event.target.value })}
              />
            </div>
            <div className="space-y-1 sm:col-span-2">
              <Label htmlFor="lauf-stories">Stories (optional)</Label>
              <Input
                id="lauf-stories"
                value={neu.schluessel}
                placeholder="PERS-3, KUNDE-1 — leer: alle bereitstehenden"
                onChange={(event) => setNeu({ ...neu, schluessel: event.target.value })}
              />
            </div>
            <div className="flex gap-2 sm:col-span-3">
              <Button disabled={legtAn} onClick={() => void manuellAnlegen()}>
                Anlegen
              </Button>
              <Button variant="ghost" onClick={() => setNeu(null)}>
                Abbrechen
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="flex flex-wrap gap-2">
        {ARTEN.map((wert) => (
          <button
            key={wert}
            type="button"
            onClick={() => setArt(wert)}
            className={`rounded-md border px-3 py-1.5 text-sm ${art === wert ? 'border-primary bg-primary/10' : ''}`}
          >
            {wert === 'alle' ? 'Alle' : LAUF_ART_LABEL[wert]}
          </button>
        ))}
      </div>

      {laedt && !daten ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Testläufe werden geladen …
        </div>
      ) : laeufe.length === 0 ? (
        <div className="rounded-lg border border-dashed p-6 text-sm text-muted-foreground">
          Noch kein Lauf gemeldet. Nach dem nächsten Push auf dev erscheinen hier die Ergebnisse.
        </div>
      ) : (
        <>
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Trend</CardTitle>
              <CardDescription>Grün und rot je Lauf, älteste links.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex h-24 items-end gap-1" role="img" aria-label="Verlauf grüner und roter Tests">
                {trend.map((lauf) => {
                  const summe = lauf.bestanden + lauf.fehlgeschlagen;
                  const hoehe = Math.max(4, Math.round((summe / hoechstes) * 96));
                  const rotAnteil = summe > 0 ? lauf.fehlgeschlagen / summe : 0;
                  return (
                    <div
                      key={lauf.id}
                      className="flex w-3 flex-col justify-end overflow-hidden rounded-sm bg-emerald-500/80"
                      style={{ height: hoehe }}
                      title={`${LAUF_ART_LABEL[lauf.art] ?? lauf.art} ${zeit(lauf.createdAt)}: ${lauf.bestanden} grün, ${lauf.fehlgeschlagen} rot`}
                    >
                      {lauf.fehlgeschlagen > 0 && (
                        <div className="bg-red-500" style={{ height: `${Math.max(rotAnteil * 100, 8)}%` }} />
                      )}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="overflow-x-auto p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-8" />
                    <TableHead>Wann</TableHead>
                    <TableHead>Art</TableHead>
                    <TableHead>Stand</TableHead>
                    <TableHead className="text-right">Grün</TableHead>
                    <TableHead className="text-right">Rot</TableHead>
                    <TableHead className="text-right">Übersprungen</TableHead>
                    <TableHead className="text-right">Coverage</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {laeufe.map((lauf) => (
                    <Fragment key={lauf.id}>
                      <TableRow className="cursor-pointer" onClick={() => void aufklappen(lauf)}>
                        <TableCell>
                          {offen === lauf.id ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                        </TableCell>
                        <TableCell className="whitespace-nowrap text-sm">{zeit(lauf.createdAt)}</TableCell>
                        <TableCell className="text-sm">
                          {LAUF_ART_LABEL[lauf.art] ?? lauf.art}
                          {lauf.art === 'manuell' ? (
                            <span className="ml-2 text-xs text-muted-foreground">
                              {lauf.titel}
                              {lauf.abgeschlossenAt ? '' : ' · läuft'}
                            </span>
                          ) : (
                            <Badge variant="outline" className="ml-2">
                              {lauf.umgebung === 'live' ? 'main' : 'dev'}
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {lauf.version ?? '—'}
                          {lauf.commit ? ` · ${lauf.commit.slice(0, 7)}` : ''}
                          {lauf.runUrl && (
                            <a
                              href={lauf.runUrl}
                              target="_blank"
                              rel="noreferrer"
                              className="ml-1 inline-flex items-center"
                              onClick={(event) => event.stopPropagation()}
                              aria-label="Lauf in GitHub öffnen"
                            >
                              <ExternalLink className="h-3 w-3" />
                            </a>
                          )}
                        </TableCell>
                        <TableCell className="text-right text-sm">{lauf.bestanden}</TableCell>
                        <TableCell
                          className={`text-right text-sm ${lauf.fehlgeschlagen > 0 ? 'font-medium text-red-600' : ''}`}
                        >
                          {lauf.fehlgeschlagen}
                        </TableCell>
                        <TableCell className="text-right text-sm text-muted-foreground">{lauf.uebersprungen}</TableCell>
                        <TableCell className="text-right text-sm">
                          {lauf.coverage !== null ? `${lauf.coverage.toLocaleString('de-DE')} %` : '—'}
                        </TableCell>
                      </TableRow>
                      {offen === lauf.id && (
                        <TableRow>
                          <TableCell colSpan={8} className="bg-muted/30">
                            <LaufDetail ergebnisse={details[lauf.id]} />
                          </TableCell>
                        </TableRow>
                      )}
                    </Fragment>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </>
      )}

      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Verwaiste Tests</CardTitle>
            <CardDescription>
              Tests, deren Schlüssel es im Katalog nicht (mehr) gibt — Tag oder Katalog anpassen.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {(daten?.verwaist.length ?? 0) === 0 ? (
              <p className="text-sm text-muted-foreground">Keine.</p>
            ) : (
              <ul className="space-y-2 text-sm">
                {daten?.verwaist.map((eintrag) => (
                  <li key={eintrag.key}>
                    <span className="font-mono text-xs">{eintrag.key}</span>
                    <span className="block break-all text-xs text-muted-foreground">
                      {eintrag.tests.slice(0, 3).join(', ')}
                      {eintrag.tests.length > 3 ? ` … (${eintrag.tests.length})` : ''}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Wackelnde Tests</CardTitle>
            <CardDescription>
              Getaggte Tests, die in den letzten zehn Läufen ihrer Art mal grün, mal rot waren.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {(daten?.instabil.length ?? 0) === 0 ? (
              <p className="text-sm text-muted-foreground">Keine.</p>
            ) : (
              <ul className="space-y-1 text-sm">
                {daten?.instabil.map((eintrag) => (
                  <li key={`${eintrag.art}-${eintrag.ref}`} className="flex items-start gap-1.5">
                    <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600" />
                    <span className="break-all text-xs">
                      {LAUF_ART_LABEL[eintrag.art] ?? eintrag.art}: {eintrag.ref}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function LaufDetail({ ergebnisse }: { ergebnisse?: LaufErgebnisDto[] }) {
  if (!ergebnisse) {
    return (
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <Loader2 className="h-3.5 w-3.5 animate-spin" /> lädt …
      </div>
    );
  }
  if (ergebnisse.length === 0) {
    return <p className="text-xs text-muted-foreground">Keine getaggten oder roten Tests in diesem Lauf.</p>;
  }
  const rot = ergebnisse.filter((e) => e.status === 'fehlgeschlagen');
  const getaggt = ergebnisse.filter((e) => e.status !== 'fehlgeschlagen');
  return (
    <div className="space-y-3">
      {rot.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-medium text-red-700 dark:text-red-300">Rot ({rot.length})</p>
          {rot.map((e) => (
            <div key={`${e.ref}-${e.storyKey ?? ''}`} className="text-xs">
              <span className="break-all">{e.ref}</span>
              {e.storyKey && <span className="ml-2 font-mono text-muted-foreground">{e.storyKey}</span>}
              {e.meldung && (
                <pre className="mt-1 max-h-32 overflow-auto whitespace-pre-wrap rounded bg-muted p-2 text-[11px]">
                  {e.meldung}
                </pre>
              )}
            </div>
          ))}
        </div>
      )}
      {getaggt.length > 0 && (
        <div className="space-y-1">
          <p className="text-xs font-medium text-muted-foreground">Getaggt ({getaggt.length})</p>
          <ul className="grid gap-x-4 gap-y-0.5 text-xs sm:grid-cols-2">
            {getaggt.map((e) => (
              <li key={`${e.ref}-${e.storyKey ?? ''}`} className="break-all">
                <span className="font-mono text-muted-foreground">{e.storyKey}</span> {e.name}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
